import { useQueryClient } from '@tanstack/react-query';
import { useMe } from './use-me.ts';
import { useOptimisticDismissal } from './use-optimistic-dismissal.ts';
import { useTokenStore } from '../api/use-api.ts';
import { dismissRoleIntro as postDismissRoleIntro } from '../api/me.ts';
import { ROLE_INTRO_CONTENT, type RoleIntroContent, type RoleIntroRoleKey } from './role-intro-content.ts';
import {
  nextRoleIntroRole,
  replayRoleIntro,
  ROLE_INTRO_REPLAY_PARAM,
} from './role-intro-selection.ts';
import { useSearchParams } from 'react-router-dom';


export interface RoleIntroState {
  roleKey: RoleIntroRoleKey;
  content: RoleIntroContent;
  /** Records the dismissal server-side and clears the overlay optimistically. */
  dismiss: () => void;
  dismissal: { readonly error: unknown };
}

export function useRoleIntro(): RoleIntroState | null {
  const me = useMe();
  const store = useTokenStore();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  // Per role, not one slot: a dual-persona account (client_contact + support_worker both carry
  // content) meets one walkthrough per role, and dismissing the second must not bring back the first.
  const dismissal = useOptimisticDismissal(
    (roleKey: RoleIntroRoleKey) => postDismissRoleIntro(roleKey, store),
    () => { void qc.invalidateQueries({ queryKey: ['me'] }); },
  );

  const heldRoles = new Set(
    (me.data?.memberships ?? []).map((m) => m.roleInPractice as RoleIntroRoleKey),
  );
  const dismissedRoles = new Set(me.data?.roleIntroDismissedRoles ?? []);

  // A REPLAY outranks the first-login pick and ignores dismissal — that is what asking to see
  // it again means. Closing one only drops the parameter: there is no new "seen" fact to record,
  // and posting a dismissal for a role whose walkthrough is being re-watched reads backwards.
  const replayKey = replayRoleIntro(params.get(ROLE_INTRO_REPLAY_PARAM), heldRoles);
  if (replayKey !== undefined) {
    const replayContent = ROLE_INTRO_CONTENT[replayKey];
    if (replayContent !== undefined) {
      return {
        roleKey: replayKey,
        content: replayContent,
        dismissal: { error: null },
        dismiss: () => {
          const next = new URLSearchParams(params);
          next.delete(ROLE_INTRO_REPLAY_PARAM);
          setParams(next, { replace: true });
        },
      };
    }
  }

  const roleKey = nextRoleIntroRole(heldRoles, dismissedRoles, new Set([...heldRoles].filter(dismissal.hidden)));
  if (roleKey === undefined) return null;

  const content = ROLE_INTRO_CONTENT[roleKey];
  if (content === undefined) return null;

  return {
    roleKey,
    content,
    dismissal: dismissal.failureOf(roleKey),
    dismiss: () => dismissal.dismiss(roleKey),
  };
}
